import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { useShallow } from "zustand/react/shallow";
import { Button } from "components/common/Button";
import { TextInput } from "components/common/TextInput";
import { SectionRule } from "components/common/SectionRule";
import { navigatorButtonClasses } from "components/common/navigatorStyles";
import { Badge } from "components/common/Badge";
import { Card } from "components/common/Card";
import { Callout } from "components/common/Callout";
import { SelectCheckbox } from "components/common/SelectCheckbox";
import { plural } from "components/common/ImpactRow";
import { showNotificationGlobal } from "stores/useNotificationStore";
import {
  abandonIncompleteOrg,
  attachSubscription,
  createOrg,
  fetchOrgLimits,
  fetchOrgReadiness,
  type OrgLimits,
  type ReadinessCheck,
} from "@/lib/api/organizations";
import { useOrgContextStore } from "@/components/main/stores/orgContextStore";
import type { CreatedOrgNotice } from "./OrgCreatedPage";
import { orgCreateRefusal } from "@/components/main/organizations/orgCreateRefusal";

const DEFAULT_SPACE_NAME = "General";

/// SI decimal, matching how the catalog stores the grant.
const formatBytes = (bytes: number) =>
  bytes >= 1_000_000_000
    ? `${+(bytes / 1_000_000_000).toFixed(1)} GB`
    : `${Math.round(bytes / 1_000_000)} MB`;

/// `create_org` writes across Postgres, FGA and Redis, and two of those writes
/// are best-effort, so the checklist is re-read until every line lands. The
/// cap exists so a genuinely-stuck write reports itself instead of spinning.
const READINESS_ATTEMPTS = 6;
const READINESS_DELAY_MS = 500;

const sleep = (ms: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, ms));

const message = (error: unknown, fallback: string) =>
  error instanceof Error ? error.message : fallback;

const PlanFact: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <span className="flex flex-col">
    <span
      className="font-mono text-fg-muted uppercase"
      style={{
        fontSize: "var(--font-size-xx-small)",
        letterSpacing: "0.08em",
      }}
    >
      {label}
    </span>
    <span className="text-fg-regular">{value}</span>
  </span>
);

const CheckLine: React.FC<{ check: ReadinessCheck }> = ({ check }) => (
  <div className="flex items-baseline gap-2 py-0.5">
    <span
      className={`w-3 shrink-0 font-mono ${check.ok ? "text-success" : "text-warning"}`}
      style={{ fontSize: "var(--font-size-small)" }}
      aria-hidden
    >
      {check.ok ? "✓" : "…"}
    </span>
    <span className="min-w-0 flex-1 text-fg-regular">
      {check.label}
      {check.detail && (
        <span className="pl-2 text-fg-muted-regular-small">{check.detail}</span>
      )}
    </span>
  </div>
);

/// One manifest row. The value carries its own sub-line rather than a second
/// column, so a long org name wraps under the key instead of shoving it.
const ManifestRow: React.FC<{
  label: string;
  children: React.ReactNode;
  sub?: React.ReactNode;
}> = ({ label, children, sub }) => (
  <div className="flex items-baseline gap-2 py-0.5">
    <span
      className="w-20 shrink-0 text-fg-muted"
      style={{ fontSize: "var(--font-size-small)" }}
    >
      {label}
    </span>
    <span className="min-w-0 flex-1 break-words text-fg-regular">
      {children}
      {sub && (
        <span className="mt-px block text-fg-muted-regular-small">{sub}</span>
      )}
    </span>
  </div>
);

/// Creating a non-personal organization, reached from the personal org's General
/// panel — the one org every account has.
///
/// The primary action sits in the sticky manifest rather than at the form's
/// foot: the manifest is the review, and a footer button spends most of the
/// flow below the fold.
/// What the unattached-subscription callout hands this page.
interface CarriedSubscription {
  subscription_id: string;
  plan_name: string | null;
  seats: number;
}

export const OrgCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const { refreshOrgs, switchOrg, adoptOrg, activeOrg } = useOrgContextStore(
    useShallow((s) => ({
      refreshOrgs: s.refreshOrgs,
      switchOrg: s.switchOrg,
      adoptOrg: s.adoptOrg,
      activeOrg: s.activeOrg,
    })),
  );

  // The door only exists on a settings page, so that page is where the trail
  // leads back to.
  const from = activeOrg();

  // Carried by the unattached-subscription callout, so the new org can claim it
  // without a second trip through its settings. Offered, never assumed: money
  // moves, so the tick is the consent and the attach door re-checks every rule.
  const carried = (
    useLocation().state as { attachSubscription?: CarriedSubscription } | null
  )?.attachSubscription;
  const [attachCarried, setAttachCarried] = useState(true);

  const [name, setName] = useState("");
  const [spaceName, setSpaceName] = useState(DEFAULT_SPACE_NAME);
  const [isCreating, setIsCreating] = useState(false);
  const [checks, setChecks] = useState<ReadinessCheck[] | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [limits, setLimits] = useState<OrgLimits | null>(null);

  // The new org has no period to read, so the offer it will open on is the only
  // truthful source for the figures this page states.
  useEffect(() => {
    void fetchOrgLimits()
      .then(setLimits)
      .catch(() => setLimits(null));
  }, []);

  const planLimits = limits?.default_plan?.limits;
  const seats = planLimits?.seats ?? null;
  const storage = planLimits?.storage_bytes ?? null;

  const trimmedName = name.trim();
  const trimmedSpace = spaceName.trim();

  const refusal =
    orgCreateRefusal(limits) ??
    (trimmedName.length < 2
      ? "Name the organization to continue."
      : trimmedSpace.length < 2
        ? "Name the first space. An organization without a space cannot hold content."
        : null);

  const land = (notice: CreatedOrgNotice) => {
    // Its own screen, with the org in the path so a reload keeps it. `replace`
    // so Back does not re-enter the form for an org that now exists.
    navigate(`/organizations/${notice.org_id}/created`, {
      state: { createdOrg: notice },
      replace: true,
    });
  };

  const submit = async () => {
    if (refusal || isCreating) return;
    setIsCreating(true);
    setFailure(null);
    setChecks(null);
    try {
      const org = await createOrg({
        name: trimmedName,
        first_space_name: trimmedSpace,
      });
      const notice: CreatedOrgNotice = {
        org_id: org.org_id,
        name: org.name,
        first_space_name: trimmedSpace,
      };
      // Adopt first: the switcher renders nothing for an active org missing
      // from the list, so a lagging refresh would blank the sidebar.
      adoptOrg(org);
      await refreshOrgs();
      await switchOrg(org.org_id);

      // Only confirm once every write is verified present. The org exists
      // either way, so a stuck line reports itself rather than blocking.
      let latest: ReadinessCheck[] = [];
      for (let attempt = 0; attempt < READINESS_ATTEMPTS; attempt++) {
        const readiness = await fetchOrgReadiness(org.org_id);
        latest = readiness.checks;
        setChecks(readiness.checks);
        if (readiness.ready) {
          if (carried && attachCarried) {
            notice.attached = await attachSubscription(
              org.org_id,
              carried.subscription_id,
            );
            // The switcher's dropdown reads `Org.plan_name` off the orgs list,
            // which was fetched while this org was still Free.
            if (notice.attached) await refreshOrgs();
          }
          land(notice);
          return;
        }
        if (attempt < READINESS_ATTEMPTS - 1) await sleep(READINESS_DELAY_MS);
      }

      // A half-provisioned org must not stay alive, so the attempt is rolled
      // back rather than handed over; the reconciler sweeps what the door
      // could not reach.
      const missing = latest
        .filter((c) => !c.ok)
        .map((c) => c.label)
        .join(", ");
      try {
        await abandonIncompleteOrg(org.org_id);
        setFailure(
          `Could not finish creating “${notice.name}” — ${missing || "a write"} never landed, so the attempt was rolled back. Try again.`,
        );
      } catch {
        setFailure(
          `Could not finish creating “${notice.name}” — ${missing || "a write"} never landed, and the rollback failed too. Support can clear it.`,
        );
      }
      await refreshOrgs();
    } catch (error) {
      showNotificationGlobal(
        message(error, "Could not create the organization"),
        "error",
      );
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="flex h-full flex-col overflow-y-auto px-8">
      <div
        className="mx-auto flex w-full max-w-4xl flex-col gap-4 pb-6"
        style={{ paddingTop: "10vh" }}
      >
        <span className="flex items-center gap-2 item-height">
          {from && (
            <>
              <button
                type="button"
                onClick={() => navigate("/overview")}
                className={`${navigatorButtonClasses} cursor-pointer text-fg-muted-semibold`}
              >
                <span className="truncate">{from.name}</span>
              </button>
              <span className="flex items-center text-fg-muted-regular">/</span>
            </>
          )}
          <button
            type="button"
            onClick={() =>
              from ? navigate(`/organizations/${from.org_id}`) : navigate(-1)
            }
            className={`${navigatorButtonClasses} cursor-pointer text-fg-muted-semibold`}
          >
            Settings
          </button>
          <span className="flex items-center text-fg-muted-regular">/</span>
          <span className={`${navigatorButtonClasses} text-fg-semibold`}>
            New organization
          </span>
        </span>

        <div className="flex gap-6">
          {/* ── Form ───────────────────────────────────────────── */}
          <div className="flex min-w-0 flex-1 flex-col gap-6">
            <div className="flex flex-col gap-1 px-2">
              <span className="text-fg-title">New organization</span>
              <span className="text-fg-muted-regular">
                A separate roster, plan and set of spaces.
              </span>
            </div>

            <section className="flex flex-col">
              <SectionRule label="Identity" />
              <div className="px-2">
                <TextInput
                  value={name}
                  onChange={setName}
                  placeholder="Organization name"
                  maxLength={80}
                  autoFocus
                  ariaLabel="Organization name"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") void submit();
                  }}
                />
              </div>
              <span className="px-2 pt-1 text-fg-muted-regular-small">
                A logo can be added after creation, in organization settings
                under General.
              </span>
            </section>

            <section className="flex flex-col">
              <SectionRule label="Plan" trailing="UPGRADE ANY TIME" />
              <Card variant="subtle" className="mx-2">
                <div className="flex flex-col gap-2">
                  <span className="flex items-center gap-2">
                    <span className="text-fg-semibold">
                      {limits?.default_plan?.name ?? "Free"}
                    </span>
                    <Badge label="Every organization starts here" />
                  </span>
                  <div className="flex gap-4">
                    <PlanFact label="Seats" value={seats ?? "—"} />
                    <PlanFact
                      label="Storage"
                      value={storage ? formatBytes(storage) : "—"}
                    />
                    <PlanFact label="Billing" value="None" />
                  </div>
                </div>
              </Card>
              {carried && (
                <Callout
                  variant="info"
                  title="Use the subscription you already pay for"
                  className="mx-2 mt-2"
                >
                  <label
                    className="flex cursor-pointer items-start gap-2"
                    data-testid="attach-carried-subscription"
                  >
                    <span className="mt-0.5">
                      <SelectCheckbox
                        selected={attachCarried}
                        onToggle={() => setAttachCarried(!attachCarried)}
                        size="sm"
                        ariaLabel="Attach the existing subscription to this organization"
                      />
                    </span>
                    <span>
                      {`Put this organization on ${carried.plan_name ?? "the plan"} ` +
                        `(${carried.seats} ${plural(carried.seats, "seat")}) instead of ` +
                        `${limits?.default_plan?.name ?? "Free"}. Nothing is charged — ` +
                        `it moves the subscription you already pay for onto this ` +
                        `organization, and the plan above stops applying.`}
                    </span>
                  </label>
                </Callout>
              )}
            </section>

            <section className="flex flex-col">
              <SectionRule label="First space" />
              <div className="px-2">
                <TextInput
                  value={spaceName}
                  onChange={setSpaceName}
                  placeholder="Space name"
                  maxLength={60}
                  ariaLabel="First space name"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") void submit();
                  }}
                />
              </div>
              <span className="px-2 pt-1 text-fg-muted-regular-small">
                Every model lives in a space. Each member who joins later
                receives their own.
              </span>
            </section>
          </div>

          {/* ── Manifest ───────────────────────────────────────── */}
          <Card
            variant="subtle"
            className="sticky top-0 w-64 shrink-0 self-start"
          >
            <span
              className="pb-0.5 font-mono text-fg-muted uppercase"
              style={{
                fontSize: "var(--font-size-xx-small)",
                letterSpacing: "0.1em",
              }}
            >
              Summary
            </span>
            {/* Same element as the dividers below, so all three lines in this
                card sit the same distance from the rows they separate. */}
            <div className="my-2 h-px bg-border" />
            <ManifestRow label="Organization">{trimmedName || "—"}</ManifestRow>
            <ManifestRow label="ID">
              <span className="font-mono text-fg-muted-regular-small">
                assigned on create
              </span>
            </ManifestRow>
            <div className="my-2 h-px bg-border" />
            <ManifestRow
              label="Plan"
              sub={
                seats && storage
                  ? `${seats} seats · ${formatBytes(storage)}`
                  : undefined
              }
            >
              {limits?.default_plan?.name ?? "Free"}
            </ManifestRow>
            <ManifestRow label="Period">
              <span className="font-mono text-fg-muted-regular-small">
                opens now
              </span>
            </ManifestRow>
            <div className="my-2 h-px bg-border" />
            <ManifestRow label="First space" sub="you are its manager">
              {trimmedSpace || "—"}
            </ManifestRow>
            <ManifestRow label="Your role" sub="sole member">
              Owner
            </ManifestRow>

            {checks && (
              <>
                <div className="my-2 h-px bg-border" />
                <span
                  className="mb-2 font-mono text-fg-muted uppercase"
                  style={{
                    fontSize: "var(--font-size-xx-small)",
                    letterSpacing: "0.1em",
                  }}
                >
                  Provisioning
                </span>
                {checks.map((c) => (
                  <CheckLine key={c.key} check={c} />
                ))}
              </>
            )}

            <div className="mt-3 flex flex-col gap-2 border-t border-border pt-3">
              <Button
                variant="primary"
                size="lg"
                fullWidth
                disabled={refusal !== null || isCreating}
                onClick={() => void submit()}
              >
                {isCreating
                  ? checks
                    ? "Verifying…"
                    : "Creating…"
                  : "Create organization"}
              </Button>
              <span
                className={
                  failure
                    ? "text-error"
                    : refusal
                      ? "text-warning"
                      : "text-fg-muted-regular-small"
                }
                role={failure ? "alert" : undefined}
              >
                {failure ?? refusal ?? "No charge applies."}
              </span>
              <Button
                variant="quiet-link"
                size="sm"
                onClick={() => navigate(-1)}
                disabled={isCreating}
              >
                Cancel
              </Button>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};
